import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../../services/HttpManager";
import { AdvisorSelection } from "../../../../models/EnrollmentHistoryDTO";
import FotoCelular from "../../../../components/enrollment/fotoCelular";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import AsesorasConReparto from "../../../../components/promos/asesorasConReparto";
import CursoRegalado2x1 from "../../../../components/promos/cursoRegalado2x1";
import { EligeRegalo, TarjetaPromo, dinero } from "../../../../components/promos/tarjetasPromo";
import { esBloqueoLigado } from "../../../../components/modals/payments/register/promoPasos";
import { montoEC } from "../../../../components/promos/bandaEnCamino";
import { EnCamino, KIND_DOS_X_UNO, PromoOption, advisorsPending, chargeOf, fitOf, giftOptionsOf, initialGiftKey } from "../../../../components/promos/tipos";
import { achicarImagen } from "../../../../utils/achicarImagen";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import { hoyCdmx } from "../../../../utils/horaMexico";
import { errorText } from "../../managment/enrollmentRequests/helpers";
import { EnPersonaCreada, PromoRequestCreated } from "../../../../services/v2/promoApplications.service";
import S from "./strings";

/*
	Solicitud de Promoción (docs/ESPEC_SOLICITUD_PROMO.md): la asesora dice «este pago es un 8x4 y es mío» para un alumno que YA está
	matriculado. Todo en una pantalla, de arriba abajo: Persona → curso, Pago (monto, fecha, método, comprobante), Promoción (las mismas
	tarjetas que ve Caja, por el monto), Asesoras (yo al 100 % por omisión) y Notas. «Enviar a Caja» crea el ticket con el borrador de la
	promo; Caja lo aplica en Registrar Pago. Si el monto no cuadra se avisa, pero se deja enviar (Caja decide con el monto real).
*/

const http = HttpManager.getInstance();
const MAX_BYTES = 10 * 1024 * 1024;   // el mismo tope que Adjuntos en el servidor
const MAX_FILES = 5;
let siguiente = 1;

interface Comprobante { key: number; file: File; preview: string | null }
export interface PersonaElegida { id: number; name: string; identityCode?: string | null }
/** Prellenado desde el CRM / WappChat: `state.solicitarPromo = { studentId, enrollmentId?, name?, identityCode? }`. */
export interface SolicitarPromoInicial { studentId: number; enrollmentId?: number | null; name?: string | null; identityCode?: string | null }

interface Props {
	campusId: number;
	campusName: string;
	sym: string;
	movil: boolean;
	inicial?: SolicitarPromoInicial | null;
	onClose: () => void;
	/** Se creó el ticket: la lista de atrás se refresca. */
	onSent: (r: PromoRequestCreated) => void;
	/** Se dejó lista «En Caja» (sin comprobante): la lista de atrás pasa a la pestaña En Camino. */
	onSentEnCaja?: (e: EnCamino) => void;
}

interface Matricula { enrollmentId: number; courseName: string; scheduleText: string; promos: number; libres: number }

/*
	Por pasos, como la Matrícula (Esteban, 2026-11-01: "que se parezca a Matrículas"): una pregunta a la vez, barra de avance,
	palomita en cada paso terminado y un Revisar al final que lleva directo a lo que falte.
*/
type PasoKey = "persona" | "pago" | "promocion" | "revisar";
const PASOS: { key: PasoKey; titulo: string; pregunta: string; icono: any }[] = [
	{ key: "persona", titulo: "Persona", pregunta: "¿De quién es la promoción?", icono: solid("user") },
	{ key: "pago", titulo: "Pago", pregunta: "¿Cómo paga?", icono: solid("money-bill-wave") },
	{ key: "promocion", titulo: "Promoción", pregunta: "¿Con qué promoción?", icono: solid("tags") },
	{ key: "revisar", titulo: "Revisar", pregunta: "Revisa y envía", icono: solid("circle-check") },
];

const gris: React.CSSProperties = { fontSize: 14, color: "var(--wa-apagado)" };
/** Fuera del componente: definidos adentro se vuelven a montar en cada tecla y el campo pierde el foco. */
const Opcion: React.FC<{ activa: boolean; icono: any; titulo: string; sub?: React.ReactNode; disabled?: boolean; onClick: () => void }> = ({ activa, icono, titulo, sub, disabled, onClick }) => (
	<button type="button" className={"waOpcion" + (activa ? " activa" : "")} disabled={disabled} onClick={onClick} aria-pressed={activa}>
		<FontAwesomeIcon icon={icono} className="waOpcionIcono" />
		<span style={{ textAlign: "center" }}>{titulo}</span>
		{sub ? <span style={{ ...gris, fontWeight: 500, textAlign: "center" }}>{sub}</span> : null}
		{activa ? <FontAwesomeIcon icon={solid("circle-check")} className="waOpcionCheck" /> : null}
	</button>
);
const Campo: React.FC<{ t: string; falta?: boolean; className?: string; children: React.ReactNode }> = ({ t, falta, className, children }) => (
	<div className={(className || "col-md-6") + (falta ? " waPasoFalta" : "")} style={{ marginBottom: 12 }}>
		<label style={{ position: "static", fontSize: 14, color: falta ? "var(--wa-oro)" : "var(--wa-apagado)", display: "block", marginBottom: 3, fontWeight: 500 }}>{t}{falta ? " · falta" : ""}</label>
		{children}
	</div>
);

const SolicitarPromocion: React.FC<Props> = ({ campusId, campusName, sym, movil, inicial, onClose, onSent, onSentEnCaja }) => {
	// ---------------------------------------------------------------- persona
	const [persona, setPersona] = useState<PersonaElegida | null>(inicial ? { id: inicial.studentId, name: inicial.name || "", identityCode: inicial.identityCode || null } : null);
	const [q, setQ] = useState<string>("");
	const [results, setResults] = useState<any[]>([]);
	const [searching, setSearching] = useState<boolean>(false);
	const timer = useRef<any>(null);
	// ---------------------------------------------------------------- curso y promos (lo dice _promoOptions)
	const [opciones, setOpciones] = useState<PromoOption[] | null>(null);
	const [enrollmentId, setEnrollmentId] = useState<number | null>(inicial?.enrollmentId || null);
	const [promoKey, setPromoKey] = useState<string | null>(null);
	const [giftKey, setGiftKey] = useState<string | null>(null);
	const [gift, setGift] = useState<{ enrollmentId: number; studentId: number; label: string } | null>(null);
	const [giftIssues, setGiftIssues] = useState<string[] | null>(null);
	// ---------------------------------------------------------------- cómo paga: «Ya Pagó» (comprobante, ticket) o «En Caja» (sin comprobante, queda Por Cobrar)
	const [enCaja, setEnCaja] = useState<boolean>(false);
	const [listoCaja, setListoCaja] = useState<EnPersonaCreada | null>(null);
	// ---------------------------------------------------------------- pago
	const [total, setTotal] = useState<string>("");
	const [paymentDate, setPaymentDate] = useState<string>(hoyCdmx());
	const [methods, setMethods] = useState<any[]>([]);
	const [methodId, setMethodId] = useState<number>(0);
	const [accountId, setAccountId] = useState<number>(0);
	const [reference, setReference] = useState<string>("");
	const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
	const [celular, setCelular] = useState<boolean>(false);
	const archivo = useRef<HTMLInputElement | null>(null);
	// ---------------------------------------------------------------- asesoras, notas, envío
	const [advisors, setAdvisors] = useState<AdvisorSelection[] | null>(null);
	const [notas, setNotas] = useState<string>("");
	const [enviando, setEnviando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [listo, setListo] = useState<PromoRequestCreated | null>(null);

	const amount = enCaja ? 0 : Math.round((Number(total) || 0) * 100) / 100;
	const method = useMemo(() => methods.find((m: any) => Number(m.id) === methodId) || null, [methods, methodId]);
	const accounts: any[] = method?.accounts || [];

	useEffect(() => {
		http.paymentService.registerMethods(campusId, true).then((r: any) => {
			const list = r.data || [];
			setMethods(list);
			setMethodId((prev) => (prev && list.some((m: any) => Number(m.id) === prev)) ? prev : (list.length ? Number(list[0].id) : 0));
		}).catch((e: any) => setError(errorText(e)));
	}, [campusId]);
	useEffect(() => {
		if (!accounts.some((a: any) => Number(a.id) === accountId)) setAccountId(accounts.length === 1 ? Number(accounts[0].id) : 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [methodId, methods]);
	useEffect(() => () => comprobantes.forEach((c) => c.preview && URL.revokeObjectURL(c.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

	// Las opciones de la persona: primero sin monto (para listar sus cursos); con monto escrito, otra vez para que diga si cuadra.
	const pedido = useRef<number>(0);
	useEffect(() => {
		if (!persona) { setOpciones(null); return; }
		const n = ++pedido.current;
		const t = setTimeout(() => {
			http.paymentService.promoOptions(campusId, { studentId: persona.id, amount, paymentDate }).then((r: any) => {
				if (n !== pedido.current) return;
				setOpciones(r.data?.options || []);
				setError(null);
			}).catch((e: any) => { if (n === pedido.current) { setOpciones([]); setError(errorText(e)); } });
		}, opciones === null ? 0 : 500);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [persona?.id, amount, paymentDate, campusId]);

	const matriculas: Matricula[] = useMemo(() => {
		const map: Record<number, Matricula> = {};
		(opciones || []).forEach((o) => {
			const m = map[o.enrollmentId] || (map[o.enrollmentId] = { enrollmentId: o.enrollmentId, courseName: o.courseName, scheduleText: o.scheduleText, promos: 0, libres: 0 });
			m.promos++;
			if (!o.blocked) m.libres++;
		});
		return Object.values(map);
	}, [opciones]);
	// Un solo curso (o el que trajo el enlace): queda elegido solo.
	useEffect(() => {
		if (!matriculas.length) return;
		if (enrollmentId && matriculas.some((m) => m.enrollmentId === enrollmentId)) return;
		setEnrollmentId(matriculas.length === 1 ? matriculas[0].enrollmentId : null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [matriculas]);

	const promosDelCurso: PromoOption[] = useMemo(() => (opciones || []).filter((o) => o.enrollmentId === enrollmentId).sort((a, b) => Number(!!a.blocked) - Number(!!b.blocked)), [opciones, enrollmentId]);
	const promo: PromoOption | null = promosDelCurso.find((o) => o.enrollmentId + "-" + o.promotionId === promoKey) || null;
	// Si la promo elegida ya no viene (cambió el curso o el servidor la quitó), se suelta; al elegir, el regalo único queda puesto.
	useEffect(() => { if (promoKey && !promo && opciones !== null) { setPromoKey(null); setGiftKey(null); setGift(null); setGiftIssues(null); } }, [promoKey, promo, opciones]);
	useEffect(() => { if (promo) setGiftKey((k) => initialGiftKey(promo, k)); }, [promo]);

	const elegirPersona = (p: PersonaElegida | null) => {
		setPersona(p); setQ(""); setResults([]); setOpciones(null); setEnrollmentId(null); setPromoKey(null); setGiftKey(null); setGift(null); setGiftIssues(null);
	};
	const busqueda = useRef<number>(0);
	const buscar = (v: string) => {
		setQ(v);
		clearTimeout(timer.current);
		if (v.trim().length < 2) { busqueda.current++; setResults([]); setSearching(false); return; }
		setSearching(true);   // «Buscando…» desde la primera letra, nunca «Sin resultados» mientras espera
		const n = ++busqueda.current;   // solo cuenta la última búsqueda (una lenta no pisa a la nueva)
		timer.current = setTimeout(async () => {
			try {
				const r = await http.paymentService.registerPeople(campusId, v.trim());
				if (n === busqueda.current) setResults(r.data || []);
			} catch (e) {
				if (n === busqueda.current) setError(errorText(e));
			} finally {
				if (n === busqueda.current) setSearching(false);
			}
		}, 300);
	};

	// ---------------------------------------------------------------- comprobantes
	const agregar = async (files: File[]) => {
		setError(null);
		const nuevos: Comprobante[] = [];
		for (const original of files) {
			const file = await achicarImagen(original);
			if (file.size > MAX_BYTES) { setError(S.tooBig); continue; }
			const preview = file.type.startsWith("image/") && !/hei[cf]/i.test(file.type) ? URL.createObjectURL(file) : null;
			nuevos.push({ key: siguiente++, file, preview });
		}
		setComprobantes((c) => [...c, ...nuevos].slice(0, MAX_FILES));
	};
	const subir = async (lista: FileList | null) => {
		const files = Array.from(lista || []);
		if (archivo.current) archivo.current.value = "";
		if (files.length) await agregar(files);
	};
	const quitar = (c: Comprobante) => {
		if (c.preview) URL.revokeObjectURL(c.preview);
		setComprobantes((lista) => lista.filter((x) => x.key !== c.key));
	};

	// ---------------------------------------------------------------- qué falta, por paso
	const fit = promo && amount > 0.004 ? fitOf(chargeOf(promo), amount) : null;
	const faltanEn = (k: PasoKey): string[] => {
		if (k === "persona") {
			if (!persona) return [S.missing.person];
			return enrollmentId ? [] : [S.missing.enrollment];
		}
		if (k === "pago") {
			if (enCaja) return [];
			return [...(amount < 0.005 ? [S.missing.amount] : []), ...(comprobantes.length < 1 ? [S.missing.files] : [])];
		}
		if (k === "promocion") {
			if (!promo) return [S.missing.promo];
			if (promo.blocked) return [promo.blocked];
			const gs = giftOptionsOf(promo);
			if (gs.length && !gs.some((g) => g.key === giftKey && !g.blocked)) return [gs.length === 1 && gs[0].blocked ? gs[0].blocked : S.missing.gift];
			if (promo.kind === KIND_DOS_X_UNO) {
				if (!gift) return [S.missing.twoForOne];
				if (giftIssues === null) return [S.missing.checking];
				if (giftIssues.length) return [giftIssues.join(" ")];
			}
			return [];
		}
		const a = advisorsPending(advisors);
		return a ? [a] : [];
	};
	const pendiente: string | null = PASOS.map((p) => faltanEn(p.key)).find((l) => l.length > 0)?.[0] || null;
	/** Lo que le cuesta al cliente cambiar de promoción, para que la asesora no lo prometa a ciegas (ESPEC_PROMO_EN_CAMINO §5). */
	const costoCambio: string | null = promo && !promo.blocked && (promo.replaces || (promo.reopenedMonths || []).length) ? S.costoCambio(promo, sym, chargeOf(promo)) : null;

	// ---------------------------------------------------------------- pasos (como la matrícula: una pregunta a la vez)
	const [pasoKey, setPasoKey] = useState<PasoKey>("persona");
	const [visitados, setVisitados] = useState<PasoKey[]>(["persona"]);
	const [intentados, setIntentados] = useState<PasoKey[]>([]);
	const [haciaAtras, setHaciaAtras] = useState<boolean>(false);
	const indice = PASOS.findIndex((p) => p.key === pasoKey);
	// Cada paso empieza arriba (en el celular se quedaba a media pantalla del paso anterior), como en la Matrícula.
	const cuerpoRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => { if (cuerpoRef.current) cuerpoRef.current.scrollTop = 0; }, [pasoKey]);
	const paso = PASOS[indice];
	const avance = Math.round((indice / (PASOS.length - 1)) * 100);
	const irA = (k: PasoKey) => {
		setHaciaAtras(PASOS.findIndex((p) => p.key === k) < indice);
		setVisitados((v) => v.includes(k) ? v : [...v, k]);
		setPasoKey(k);
	};
	const avanzar = () => {
		if (faltanEn(pasoKey).length) { setIntentados((v) => v.includes(pasoKey) ? v : [...v, pasoKey]); return; }
		if (indice < PASOS.length - 1) irA(PASOS[indice + 1].key);
	};
	const pasoListo = (k: PasoKey) => visitados.includes(k) && k !== pasoKey && faltanEn(k).length < 1;
	const pasoFalta = (k: PasoKey) => visitados.includes("revisar") && k !== pasoKey && faltanEn(k).length > 0;
	const marcar = intentados.includes(pasoKey);

	const enviar = async () => {
		setIntentados(PASOS.map((p) => p.key));
		if (pendiente || !persona || !promo || enviando) {
			// Lleva al paso donde falta algo; ahí lo dice el aviso «Te falta» (sin repetirlo en rojo).
			const donde = PASOS.find((p) => faltanEn(p.key).length > 0);
			if (donde && donde.key !== pasoKey) irA(donde.key);
			return;
		}
		setEnviando(true);
		setError(null);
		try {
			if (enCaja) {
				const r = await http.promoApplicationsService.createEnPersona(campusId, {
					studentId: persona.id, enrollmentId: promo.enrollmentId, promotionId: promo.promotionId,
					...(giftKey ? { giftOptionKey: giftKey } : {}),
					...(gift ? { giftEnrollmentId: gift.enrollmentId, giftStudentId: gift.studentId } : {}),
					advisors: (advisors || []).map((a) => ({ userId: a.userId, sharePercentage: a.sharePercentage })),
					...(notas.trim() ? { notes: notas.trim() } : {}),
				});
				setListoCaja(r.data);
				onSentEnCaja?.(r.data.enCamino);
				return;
			}
			const form = new FormData();
			form.append("studentId", String(persona.id));
			form.append("enrollmentId", String(promo.enrollmentId));
			form.append("promotionId", String(promo.promotionId));
			if (giftKey) form.append("giftOptionKey", giftKey);
			if (gift) { form.append("giftEnrollmentId", String(gift.enrollmentId)); form.append("giftStudentId", String(gift.studentId)); }
			form.append("advisors", JSON.stringify((advisors || []).map((a) => ({ userId: a.userId, sharePercentage: a.sharePercentage }))));
			form.append("amount", String(amount));
			form.append("paidAt", paymentDate);
			if (methodId) form.append("paymentMethodId", String(methodId));
			if (accountId) form.append("recipientAccountId", String(accountId));
			if (reference.trim()) form.append("reference", reference.trim());
			if (notas.trim()) form.append("notes", notas.trim());
			comprobantes.forEach((c) => form.append("files[]", c.file, c.file.name));
			const r = await http.promoApplicationsService.createRequest(campusId, form);
			setListo(r.data);
			onSent(r.data);
		} catch (e) {
			setError(errorText(e));
		} finally {
			setEnviando(false);
		}
	};

	const otra = () => {
		comprobantes.forEach((c) => c.preview && URL.revokeObjectURL(c.preview));
		setPersona(null); setOpciones(null); setEnrollmentId(null); setPromoKey(null); setGiftKey(null); setGift(null); setGiftIssues(null);
		setTotal(""); setPaymentDate(hoyCdmx()); setReference(""); setComprobantes([]); setNotas(""); setError(null); setListo(null); setListoCaja(null);
		setEnCaja(false); setPasoKey("persona"); setVisitados(["persona"]); setIntentados([]); setHaciaAtras(true);
	};

	// ---------------------------------------------------------------- piezas
	const sub = (t: string, extra?: string) => <div className="waPasoSub" style={{ marginTop: 16 }}>{t}{extra ? <span style={{ fontWeight: 500, marginLeft: 6 }}>{extra}</span> : null}</div>;

	// ---------------------------------------------------------------- paso 1: persona y curso
	const pasoPersona = (
		<>
			<div className={marcar && !persona ? "waPasoFalta" : ""}>
				{persona ? (
					<div className="waElegido">
						<div><b>{persona.name || "Alumno #" + persona.id}</b>{persona.identityCode ? <div style={gris}>{persona.identityCode}</div> : null}</div>
						<button type="button" className="btn btn-outline-secondary btn-sm" disabled={enviando} onClick={() => elegirPersona(null)}>{S.change}</button>
					</div>
				) : (
					<>
						<input className="form-control" type="text" autoFocus={!movil} value={q} placeholder={S.searchPlaceholder} style={{ fontSize: 16 }} onChange={(e) => buscar(e.target.value)} />
						{q.trim().length >= 2 ? (
							<div className="waResultados">
								{searching ? <div style={{ padding: 12, ...gris }}>{S.searching}</div>
									: results.length < 1 ? <div style={{ padding: 12, ...gris }}>{S.noResults}</div>
										: results.map((st: any) => (
											<button type="button" key={"r" + st.id} className="waResultado" onClick={() => elegirPersona({ id: st.id, name: st.name, identityCode: st.identityCode })}>
												<b>{st.name}</b>
												<span style={gris}>{[st.identityCode, st.campusName].filter(Boolean).join(" · ")}</span>
											</button>
										))}
							</div>
						) : null}
					</>
				)}
			</div>
			{persona ? (
				<>
					{sub(S.enrollment)}
					{opciones === null ? <div style={gris}>{S.loadingEnrollments}</div>
						: matriculas.length < 1 ? <div className="waAviso">{S.noEnrollments}</div>
							: (
								<div className="waOpciones" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
									{matriculas.map((m) => (
										<Opcion key={"m" + m.enrollmentId} disabled={enviando} activa={enrollmentId === m.enrollmentId} icono={solid("book-open")} titulo={cursoCorto(m.courseName) || m.courseName}
											sub={<>{horarioBonito(m.scheduleText, { plural: true, separador: " " }) || m.scheduleText}
												{m.libres < m.promos ? <div>{m.promos - m.libres} {m.promos - m.libres === 1 ? "promo bloqueada" : "promos bloqueadas"}</div> : null}</>}
											onClick={() => { setEnrollmentId(m.enrollmentId); setPromoKey(null); setGiftKey(null); setGift(null); setGiftIssues(null); }} />
									))}
								</div>
							)}
				</>
			) : null}
		</>
	);

	// ---------------------------------------------------------------- paso 2: cómo paga y comprobante
	const pasoPago = (
		<>
			<div className="waOpciones">
				<Opcion disabled={enviando} activa={!enCaja} icono={solid("receipt")} titulo={S.alreadyPaid} onClick={() => { setEnCaja(false); setError(null); }} />
				<Opcion disabled={enviando} activa={enCaja} icono={solid("cash-register")} titulo={S.atCashier} onClick={() => { setEnCaja(true); setError(null); }} />
			</div>
			{enCaja ? null : <>
				<div className="row">
					<Campo t={S.amount + " (" + sym + ")"} falta={marcar && amount < 0.005}>
						<input className="form-control" type="number" inputMode="decimal" min="0" step="0.01" value={total} disabled={enviando}
							style={{ fontSize: movil ? 20 : 18, fontWeight: 700, minHeight: movil ? 48 : undefined }} onChange={(e) => setTotal(e.target.value)} />
					</Campo>
					<Campo t={S.date}><input className="form-control" type="date" value={paymentDate} max={hoyCdmx()} disabled={enviando} onChange={(e) => setPaymentDate(e.target.value)} /></Campo>
					<Campo t={S.method}><select className="form-select" value={methodId} disabled={enviando} onChange={(e) => setMethodId(Number(e.target.value))}>
						{methods.map((m: any) => <option key={"m" + m.id} value={m.id}>{m.name}</option>)}</select></Campo>
					<Campo t={S.account}><select className="form-select" value={accountId} disabled={enviando || accounts.length < 1} onChange={(e) => setAccountId(Number(e.target.value))}>
						{accounts.length !== 1 ? <option value={0}>—</option> : null}
						{accounts.map((a: any) => <option key={"a" + a.id} value={a.id}>{a.name}</option>)}</select></Campo>
					{method?.referenceRequired ? <Campo t={S.reference}><input className="form-control" value={reference} disabled={enviando} onChange={(e) => setReference(e.target.value)} /></Campo> : null}
				</div>
				<div className={"waTarjeta" + (marcar && comprobantes.length < 1 ? " waPasoFalta" : "")} style={{ marginTop: 4 }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
						<b>{S.vouchers}</b>
						{comprobantes.length < MAX_FILES ? (
							<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
								<button type="button" className={"btn btn-sm " + (comprobantes.length < 1 ? "btn-primary" : "btn-outline-secondary")} style={{ minHeight: 40 }} disabled={enviando} onClick={() => archivo.current?.click()}>
									<FontAwesomeIcon icon={comprobantes.length > 0 ? solid("plus") : solid("camera")} />&nbsp;{comprobantes.length > 0 ? S.addVoucher : S.takePhoto}
								</button>
								{!movil ? <button type="button" className="btn btn-sm btn-outline-secondary" style={{ minHeight: 40 }} disabled={enviando} onClick={() => setCelular(true)}><FontAwesomeIcon icon={solid("mobile-screen")} />&nbsp;{S.usePhone}</button> : null}
							</div>
						) : null}
					</div>
					<input ref={archivo} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => subir(e.target.files)} />
					{comprobantes.map((c) => (
						<div key={c.key} className="waArchivo">
							<button type="button" aria-label="Ver archivo" style={{ width: 44, height: 44, flex: "0 0 44px", border: "1px solid var(--wa-filo)", borderRadius: 8, overflow: "hidden", background: "var(--wa-papel)", padding: 0, display: "grid", placeItems: "center", color: "var(--wa-apagado)", fontSize: 18 }}
								onClick={() => abrirArchivo({ origen: "Comprobantes de pago", nombre: c.file.name, urlLista: c.preview, cargar: () => Promise.resolve(c.file) })}>
								{c.preview ? <img src={c.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : <FontAwesomeIcon icon={solid("file-pdf")} />}
							</button>
							<span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{c.file.name}</span>
							<button type="button" className="btn btn-link btn-sm" disabled={enviando} onClick={() => quitar(c)} aria-label="Quitar"><FontAwesomeIcon icon={solid("xmark")} /></button>
						</div>
					))}
				</div>
				{celular ? <FotoCelular campusId={campusId} tipo="comprobante" onFoto={(f) => agregar([f])} onClose={() => setCelular(false)} /> : null}
			</>}
		</>
	);

	// ---------------------------------------------------------------- paso 3: promoción
	const pasoPromocion = (
		<>
			{!enCaja && amount < 0.005 ? <div className="waAviso">{S.typeAmount}</div>
				: opciones === null ? <div style={gris}>{S.loadingPromos}</div>
				: promosDelCurso.length < 1 ? <div className="waAviso">{S.noPromos}</div>
					: promosDelCurso.map((o) => {
						const k = o.enrollmentId + "-" + o.promotionId;
						return <React.Fragment key={k}><TarjetaPromo o={o} sym={sym} fit={fitOf(chargeOf(o), amount)} cargo={chargeOf(o)} recargo={Number(o.surcharges) || 0}
							selected={promoKey === k} radioName="spPromo" disabled={enviando}
							onPick={() => { setPromoKey(k); setGiftKey(initialGiftKey(o)); setGift(null); setGiftIssues(null); }} />
						{esBloqueoLigado(o) ? <div className="waAviso" style={{ marginTop: 0 }}>{S.linkedHint}</div> : null}
						</React.Fragment>;
					})}
			{!enCaja && promo && fit && fit.fits !== "exacto" ? <div className="waAviso">{S.notFits(promo.name, dinero(sym, fit.diff), fit.fits === "faltan")}</div> : null}
			{promo ? <EligeRegalo o={promo} sym={sym} giftKey={giftKey} disabled={enviando} onChange={setGiftKey} /> : null}
			{promo && promo.kind === KIND_DOS_X_UNO && persona ? (
				<CursoRegalado2x1 campusId={campusId} studentId={persona.id} baseEnrollmentId={promo.enrollmentId} giftEnrollmentId={gift?.enrollmentId || null} disabled={enviando}
					pinned={gift ? { enrollmentId: gift.enrollmentId, studentId: gift.studentId, name: gift.label } : null}
					serverError={errorText} onError={setError}
					onPick={(v) => { setGift({ enrollmentId: v.enrollmentId, studentId: v.studentId || persona.id, label: v.name }); setGiftIssues(null); }} onIssues={setGiftIssues} />
			) : null}
			{costoCambio ? <div className="waAviso">{costoCambio}</div> : null}
		</>
	);

	// ---------------------------------------------------------------- paso 4: revisar, asesoras y notas
	const totalFaltan = PASOS.reduce((n, p) => n + faltanEn(p.key).length, 0);
	const matricula = matriculas.find((m) => m.enrollmentId === enrollmentId);
	const resumen: Record<PasoKey, string> = {
		persona: persona ? [persona.name, matricula ? cursoCorto(matricula.courseName) || matricula.courseName : ""].filter(Boolean).join(" · ") : "",
		pago: enCaja ? S.atCashier : [amount > 0.004 ? montoEC(sym, amount) : "", comprobantes.length ? comprobantes.length + (comprobantes.length === 1 ? " comprobante" : " comprobantes") : ""].filter(Boolean).join(" · "),
		promocion: promo ? promo.name + (gift ? " · " + gift.label : "") : "",
		revisar: "",
	};
	const pasoRevisar = (
		<>
			<div className={"waMeta" + (totalFaltan < 1 ? " lista" : "")}>
				<FontAwesomeIcon icon={totalFaltan < 1 ? solid("circle-check") : solid("flag-checkered")} className="waMetaIcono" />
				<div>
					<div style={{ fontSize: 18, fontWeight: 700 }}>{enviando ? S.sending : totalFaltan < 1 ? "¡Todo listo!" : totalFaltan === 1 ? "Falta 1 cosa" : `Faltan ${totalFaltan} cosas`}</div>
					<div style={gris}>{persona?.name || ""}{promo ? " · " + promo.name : ""}</div>
				</div>
			</div>
			{PASOS.filter((p) => p.key !== "revisar").map((p) => {
				const faltan = faltanEn(p.key);
				const mal = faltan.length > 0;
				return (
					<button type="button" key={"rv" + p.key} className={"waRevisa" + (mal ? " falta" : "")} disabled={enviando} onClick={() => irA(p.key)}>
						<FontAwesomeIcon icon={mal ? solid("circle-exclamation") : solid("circle-check")} className="waRevisaIcono" />
						<span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
							<b>{p.titulo}</b>{resumen[p.key] ? <span style={{ ...gris, display: "block" }}>{resumen[p.key]}</span> : null}
							{mal ? <span className="waRevisaFalta">Falta: {faltan.join(", ")}</span> : null}
						</span>
						<span className="waRevisaIr">{mal ? "Arreglar" : "Editar"} <FontAwesomeIcon icon={solid("arrow-right")} /></span>
					</button>
				);
			})}
			{!enCaja && promo && fit && fit.fits !== "exacto" ? <div className="waAviso">{S.notFits(promo.name, dinero(sym, fit.diff), fit.fits === "faltan")}</div> : null}
			{costoCambio ? <div className="waAviso">{costoCambio}</div> : null}
			{sub(S.advisors)}
			<div className="spAsesoras"><AsesorasConReparto campusId={promo?.campusId || campusId} value={advisors} onChange={setAdvisors} disabled={enviando} movil={movil} defaultSelf invalid={marcar} /></div>
			{sub(S.notes)}
			<textarea className="form-control" rows={2} value={notas} disabled={enviando} placeholder={S.notesPlaceholder} onChange={(e) => setNotas(e.target.value)} />
		</>
	);

	const contenido: Record<PasoKey, React.ReactNode> = { persona: pasoPersona, pago: pasoPago, promocion: pasoPromocion, revisar: pasoRevisar };
	const faltaAqui = faltanEn(pasoKey);

	// ---------------------------------------------------------------- enviada
	const terminado = (titulo: string, detalle?: string | null, aviso?: string | null) => (
		<div style={{ maxWidth: 520, margin: "24px auto", textAlign: "center" }}>
			<div className="waMeta lista" style={{ justifyContent: "center" }}>
				<FontAwesomeIcon icon={solid("circle-check")} className="waMetaIcono" />
				<div style={{ textAlign: "left" }}>
					<div style={{ fontSize: 18, fontWeight: 700 }}>{titulo}</div>
					{detalle ? <div style={gris}>{detalle}</div> : null}
				</div>
			</div>
			{aviso ? <div className="waAviso" style={{ textAlign: "left" }}>{aviso}</div> : null}
		</div>
	);
	const cuerpoListo = listoCaja ? terminado(listoCaja.message || S.sentCaja(listoCaja.enCamino.promoName, montoEC(listoCaja.enCamino.currencySymbol || sym, listoCaja.enCamino.minToday)))
		: listo ? terminado(S.sent, S.sentTicket(listo.intentId) + (listo.promoName ? " · " + listo.promoName : ""), listo.message && listo.message !== S.sent ? listo.message : null) : null;

	return (
		<Modal className="waModulo waPasos" show onHide={enviando ? undefined : onClose} size="lg" centered scrollable backdrop="static" fullscreen="sm-down" enforceFocus={false}>
			<Modal.Header closeButton={!enviando} style={{ flexDirection: "column", alignItems: "stretch", gap: 10, position: "relative" }}>
				<Modal.Title style={{ fontSize: movil ? 16 : 18, paddingRight: 32 }}>
					{S.requestTitle}{campusName ? <span style={{ fontSize: 14, fontWeight: 500, color: "var(--wa-apagado)", marginLeft: 8 }}>{campusName}</span> : null}
				</Modal.Title>
				{listo || listoCaja ? null : (
					<div className="waAvance" aria-label={`Paso ${indice + 1} de ${PASOS.length}`}>
						<div className="waAvanceBarra"><div style={{ width: `${avance}%` }} /></div>
						<div className="waAvancePasos">
							{PASOS.map((p, i) => {
								const clase = p.key === pasoKey ? "actual" : pasoListo(p.key) ? "listo" : pasoFalta(p.key) ? "pendiente" : visitados.includes(p.key) ? "visto" : "";
								return (
									<button type="button" key={"av" + p.key} className={"waAvancePaso " + clase} title={p.titulo} disabled={enviando || !visitados.includes(p.key)} onClick={() => irA(p.key)}>
										<span className="waAvancePunto">{clase === "listo" ? <FontAwesomeIcon icon={solid("check")} /> : clase === "pendiente" ? "!" : i + 1}</span>
										<span className="waAvanceNombre">{p.titulo}</span>
									</button>
								);
							})}
						</div>
					</div>
				)}
			</Modal.Header>
			<Modal.Body ref={cuerpoRef as any} style={{ textAlign: "left", fontSize: 16, padding: movil ? "16px 16px 24px" : "22px 28px 28px" }}>
				{listo || listoCaja ? cuerpoListo : (
					<div style={{ maxWidth: 760, margin: "0 auto" }}>
						<div key={pasoKey} className={"waPaso waPasoCabeza " + (haciaAtras ? "atras" : "adelante")}>
							<span className="waPasoIcono"><FontAwesomeIcon icon={paso.icono} /></span>
							<div>
								<div className="waPasoNumero">Paso {indice + 1} de {PASOS.length}</div>
								<h4 className="waPasoPregunta">{paso.pregunta}</h4>
							</div>
						</div>
						{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
						{marcar && faltaAqui.length > 0 ? <div className="waFaltaAviso"><FontAwesomeIcon icon={solid("circle-exclamation")} />&nbsp;Te falta: {faltaAqui.join(" ")}</div> : null}
						{/* Todos los pasos se quedan montados (solo se esconden): el curso regalado del 2x1 y las asesoras no se pierden al ir y volver. */}
						{PASOS.map((p) => (
							<div key={"c" + p.key} className={p.key === pasoKey ? "waPaso " + (haciaAtras ? "atras" : "adelante") : undefined} style={p.key === pasoKey ? undefined : { display: "none" }}>
								{contenido[p.key]}
							</div>
						))}
					</div>
				)}
			</Modal.Body>
			<Modal.Footer className="waPasosPie">
				{listo || listoCaja ? (
					<div className="waPasosBotones" style={{ marginLeft: "auto" }}>
						<button type="button" className="btn btn-outline-secondary" onClick={otra}>{S.another}</button>
						<button type="button" className="btn btn-primary" onClick={onClose}>{S.done}</button>
					</div>
				) : <>
					<div className="waGuardarCaja"><button type="button" className="btn btn-link waGuardar" disabled={enviando} onClick={onClose}>{S.cancel}</button></div>
					<div className="waPasosBotones">
						{indice > 0 ? <button type="button" className="btn btn-outline-secondary" disabled={enviando} onClick={() => irA(PASOS[indice - 1].key)}><FontAwesomeIcon icon={solid("arrow-left")} />&nbsp;Atrás</button> : null}
						{pasoKey === "revisar"
							? <button type="button" className="btn btn-primary" disabled={enviando} onClick={enviar}><FontAwesomeIcon icon={solid("paper-plane")} />&nbsp;{enviando ? S.sending : enCaja ? S.sendEnCaja : S.send}</button>
							: <button type="button" className="btn btn-primary" disabled={enviando} onClick={avanzar}>Siguiente&nbsp;<FontAwesomeIcon icon={solid("arrow-right")} /></button>}
					</div>
				</>}
			</Modal.Footer>
		</Modal>
	);
};

export default SolicitarPromocion;
